import React, { useState, useEffect, useCallback } from 'react';
import { Save, Loader2, Users, DollarSign, Package, Sparkles, Zap, Pencil, Info, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const METRIC_DEFS = [
  { key: 'families_supported', label: 'Families Supported', icon: Users, unit: '', isCurrency: false, isInteger: true, autoSupported: true, help: 'Auto-computed from completed donations ÷ Cost per Family (from Global Program Content).' },
  { key: 'total_donated', label: 'Total Donated', icon: DollarSign, unit: '$', isCurrency: true, isInteger: false, autoSupported: true, help: 'Sum of completed donations only. Excludes pending, failed, expired, cancelled, and refunded payments.' },
  { key: 'buckets_delivered', label: 'Buckets Delivered', icon: Package, unit: '', isCurrency: false, isInteger: true, autoSupported: false, help: 'Manual only — no automated operational tracking yet. Enter verified distribution totals.' },
  { key: 'hygiene_kits_delivered', label: 'Hygiene Kits Delivered', icon: Sparkles, unit: '', isCurrency: false, isInteger: true, autoSupported: false, help: 'Manual only — no automated operational tracking yet. Enter verified distribution totals.' },
];

const fmt = (val, isCurrency) => {
  const n = parseFloat(val || 0);
  if (isCurrency) return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return Math.round(n).toLocaleString();
};

const MetricCard = ({ def, config, computed, resolved, onChange }) => {
  const Icon = def.icon;
  const mode = config?.mode || (def.autoSupported ? 'auto' : 'manual');
  const autoVal = computed?.[def.key] || 0;
  const adjustment = parseFloat(config?.adjustment || 0);
  const manualVal = parseFloat(config?.manual_value || 0);

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-4 space-y-3" data-testid={`impact-metric-${def.key}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#FEE2E2] rounded flex items-center justify-center">
            <Icon className="w-4 h-4 text-[#BA1826]" />
          </div>
          <div>
            <h4 className="font-heading text-sm font-bold text-[#0A0A0A]">{def.label}</h4>
            <p className="text-[10px] text-[#4B5563] leading-tight max-w-md">{def.help}</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[9px] text-[#94A3B8] uppercase tracking-wider">Live Value</div>
          <div className="text-xl font-bold text-[#BA1826]">{fmt(resolved?.[def.key], def.isCurrency)}</div>
        </div>
      </div>

      {/* Mode Toggle */}
      <div className="flex items-center gap-1 bg-[#F1F5F9] rounded p-0.5">
        <button
          type="button"
          onClick={() => onChange({ ...config, mode: 'auto' })}
          disabled={!def.autoSupported}
          className={`flex-1 flex items-center justify-center gap-1 py-1.5 text-[11px] rounded transition-all ${mode === 'auto' ? 'bg-white text-[#0A0A0A] shadow-sm font-semibold' : 'text-[#4B5563]'} ${!def.autoSupported ? 'opacity-40 cursor-not-allowed' : ''}`}
          data-testid={`${def.key}-mode-auto`}
        >
          <Zap className="w-3 h-3" /> Auto
        </button>
        <button
          type="button"
          onClick={() => onChange({ ...config, mode: 'manual' })}
          className={`flex-1 flex items-center justify-center gap-1 py-1.5 text-[11px] rounded transition-all ${mode === 'manual' ? 'bg-white text-[#0A0A0A] shadow-sm font-semibold' : 'text-[#4B5563]'}`}
          data-testid={`${def.key}-mode-manual`}
        >
          <Pencil className="w-3 h-3" /> Manual
        </button>
      </div>

      {/* Auto mode — show auto value + adjustment */}
      {mode === 'auto' ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between bg-[#F8F9FA] border border-[#E5E7EB] rounded px-3 py-2">
            <span className="text-[11px] text-[#4B5563]">Auto value (live data)</span>
            <span className="text-sm font-bold text-[#0A0A0A]">{fmt(autoVal, def.isCurrency)}</span>
          </div>
          <div>
            <label className="text-[10px] text-[#4B5563] mb-1 block flex items-center gap-1">
              Adjustment (+/-) <Info className="w-3 h-3 text-[#94A3B8]" title="Add or subtract a fixed amount from the auto value (e.g., offline donations)." />
            </label>
            <input
              type="number"
              step={def.isCurrency ? '0.01' : '1'}
              value={adjustment}
              onChange={(e) => onChange({ ...config, mode: 'auto', adjustment: parseFloat(e.target.value) || 0 })}
              className="w-full px-2 py-1.5 text-xs border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none"
              data-testid={`${def.key}-adjustment-input`}
              placeholder="0"
            />
          </div>
          <div className="text-[10px] text-[#4B5563] flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3 text-green-600" />
            Final: <span className="font-bold text-[#0A0A0A]">{fmt(autoVal + adjustment, def.isCurrency)}</span>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <div>
            <label className="text-[10px] text-[#4B5563] mb-1 block">Manual value (overrides live data)</label>
            <input
              type="number"
              step={def.isCurrency ? '0.01' : '1'}
              min="0"
              value={manualVal}
              onChange={(e) => onChange({ ...config, mode: 'manual', manual_value: parseFloat(e.target.value) || 0 })}
              className="w-full px-2 py-1.5 text-sm border border-[#E5E7EB] rounded focus:border-[#BA1826] focus:outline-none font-semibold"
              data-testid={`${def.key}-manual-input`}
              placeholder="0"
            />
          </div>
          {def.autoSupported && autoVal > 0 && (
            <div className="text-[10px] text-[#4B5563] bg-amber-50 border border-amber-200 rounded px-2 py-1">
              Auto would be {fmt(autoVal, def.isCurrency)}. Manual value will be shown on the public site instead.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AdminImpactCounterTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/impact-counter`, { withCredentials: true });
      setData(res.data);
    } catch {
      setData({ config: {}, computed: {}, resolved: {} });
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleMetricChange = (key, newCfg) => {
    setData((prev) => ({
      ...prev,
      config: { ...prev.config, [key]: newCfg },
    }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/impact-counter`, { data: data.config }, { withCredentials: true });
      setSaved(true);
      fetchData(); // Re-fetch to refresh resolved values
      setTimeout(() => setSaved(false), 3000);
    } catch {
      alert('Save failed');
    }
    setSaving(false);
  };

  if (loading) {
    return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="impact-counter-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-lg font-bold text-[#0A0A0A]">Live Impact Counter</h2>
          <p className="text-xs text-[#4B5563]">Manage the 4 stats shown on the public homepage. Auto-computed from real donations, with manual override + adjustment options.</p>
        </div>
        <div className="flex items-center gap-2">
          {saved && (
            <span className="text-xs text-green-600 font-semibold flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> Saved
            </span>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-primary text-xs flex items-center gap-1.5 disabled:opacity-50"
            data-testid="save-impact-counter-btn"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Transparency Notice */}
      <div className="bg-[#FEFCE8] border border-[#FDE68A] rounded-lg p-3 flex items-start gap-2">
        <Info className="w-4 h-4 text-[#BA1826] flex-shrink-0 mt-0.5" />
        <div className="text-xs text-[#4B5563]">
          <strong className="text-[#0A0A0A]">Data source:</strong> Only <strong>completed</strong> donations are counted. Pending, failed, expired, cancelled, and refunded payments are excluded. Cost per Family pulls from Global Program Content (currently ${(data?.computed?.cost_per_family || 0).toFixed(2)}).
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {METRIC_DEFS.map((def) => (
          <MetricCard
            key={def.key}
            def={def}
            config={data?.config?.[def.key]}
            computed={data?.computed}
            resolved={data?.resolved}
            onChange={(newCfg) => handleMetricChange(def.key, newCfg)}
          />
        ))}
      </div>

      {/* Live Preview */}
      <div className="bg-[#0A0A0A] rounded-lg p-5" data-testid="impact-counter-preview">
        <p className="text-[10px] uppercase tracking-[0.15em] text-[#BA1826] font-semibold mb-1">Preview — Public Site</p>
        <h3 className="font-heading text-white text-lg font-bold mb-4">Your Donations at Work</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {METRIC_DEFS.map((def) => {
            const Icon = def.icon;
            return (
              <div key={def.key} className="text-center p-3 bg-white/5 rounded">
                <Icon className="w-4 h-4 text-[#BA1826] mx-auto mb-1" />
                <div className="text-2xl font-bold text-white mb-0.5">{fmt(data?.resolved?.[def.key], def.isCurrency)}</div>
                <div className="text-[9px] text-gray-400 uppercase tracking-wider">{def.label}</div>
              </div>
            );
          })}
        </div>
        <p className="text-center text-[10px] text-gray-500 mt-3">
          Totals reflect completed donations only.
        </p>
      </div>
    </div>
  );
};

export default AdminImpactCounterTab;
